import React, { useState, useEffect, useRef } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Pagination,
    TextField,
    InputAdornment,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import HoverTooltip from "./HoverTooltip";
import BillingPreviewDialog from "./BillingPreviewDialog";
import { ErrorOutline, Search, Clear, VisibilityOutlined, EditOutlined, EmailOutlined, ReceiptLongOutlined } from "@mui/icons-material";
import { TabPanel } from "@mui/lab";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { getClients } from "../redux/ducks/client";
import { formatDayShort } from "../utils/dateUtils";
import EditBillingClientDialog from "./EditBillingClientDialog";
import ContactClientDialog from "./ContactClientDialog";

const ClientsTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(3),
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
}));

const SearchRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: "center",
}));

// A long billing address was free to widen its column until the table outgrew the page.
const TRUNCATE = {
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const BillingEmail = styled(Typography)(TRUNCATE);

const ClientNameRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
}));

const RowActions = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(0.5),
    flexShrink: 0,
}));

const PaginationRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
}));

const MobileCardList = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
}));

const ClientRowCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
}));

const CardInfo = styled(Box)({
    minWidth: 0,
    flex: "1 1 auto",
});

const EmptyMobileState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4, 2),
    textAlign: "center",
}));

const NoBillingEmailChip = styled(Chip)(({ theme }) => ({
    marginLeft: theme.spacing(0.5),
}));

const MutedEmail = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    ...TRUNCATE,
}));

const limit = 50;

const dash = "—";

const formatActivityDate = (d) => formatDayShort(d) || null;

const BillingClients = ({ tabValue }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const [clients, setClients] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [editClientId, setEditClientId] = useState(null);
    const [contactTarget, setContactTarget] = useState(null);
    const [previewClient, setPreviewClient] = useState(null);
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");

    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        if (tabValue === "clients") {
            loadClients();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, page, sortBy, sortDir, search]);

    // Typing in the search box re-reads before the previous page has landed, so the table has to
    // show the answer to the last question asked rather than the last answer to arrive.
    const requestRef = useRef(0);
    const loadClients = () => {
        setLoading(true);
        const requestId = ++requestRef.current;
        dispatch(
            getClients(
                (page - 1) * limit,
                limit,
                (data) => {
                    if (requestRef.current !== requestId) return;
                    setClients(data?.clients || []);
                    setTotal(data?.total || 0);
                    setLoading(false);
                },
                { sortBy, sortDir, search: search || undefined, active: true, meta: 1 },
            ),
        );
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
        setPage(1);
    };

    const sortLabelProps = (field) => ({
        active: sortBy === field,
        direction: sortBy === field ? sortDir : "asc",
        onClick: () => handleSort(field),
    });

    const cityState = (client) => {
        const parts = [client.address?.city, client.address?.state].filter(Boolean);
        return parts.length ? parts.join(", ") : dash;
    };

    const billingEmailCell = (client) =>
        client.billingContact ? (
            <HoverTooltip title={client.billingContact}>
                <BillingEmail variant="body2">{client.billingContact}</BillingEmail>
            </HoverTooltip>
        ) : (
            <ClientNameRow>
                <HoverTooltip title="Missing billing contact email — invoices cannot be emailed to this client">
                    <NoBillingEmailChip icon={<ErrorOutline />} label="No billing email" color="warning" size="small" variant="outlined" />
                </HoverTooltip>
                {client.email && (
                    <HoverTooltip title={client.email}>
                        <MutedEmail variant="caption">{client.email}</MutedEmail>
                    </HoverTooltip>
                )}
            </ClientNameRow>
        );

    const rowActions = (client) => (
        <RowActions>
            <HoverTooltip title="View billing overview">
                <IconButton size="small" onClick={() => navigate(`/billing/client/${client._id}`)}>
                    <VisibilityOutlined fontSize="small" />
                </IconButton>
            </HoverTooltip>
            <HoverTooltip title="Preview what this client would be invoiced">
                <IconButton size="small" onClick={() => setPreviewClient(client)}>
                    <ReceiptLongOutlined fontSize="small" />
                </IconButton>
            </HoverTooltip>
            <HoverTooltip title="Edit billing settings">
                <IconButton size="small" onClick={() => setEditClientId(client._id)}>
                    <EditOutlined fontSize="small" />
                </IconButton>
            </HoverTooltip>
            <HoverTooltip title="Contact client">
                <IconButton size="small" onClick={() => setContactTarget(client)}>
                    <EmailOutlined fontSize="small" />
                </IconButton>
            </HoverTooltip>
        </RowActions>
    );

    const emptyCopy = search ? "No clients match your search" : "No clients found";

    return (
        <ClientsTabPanel value="clients">
            <Card>
                <CardHeader title="Client Management" subheader="All active clients — search, sort, and manage billing details" />
                <CardContent>
                    <SearchRow>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search clients by name or email"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchInput("")}>
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </SearchRow>
                    {loading ? (
                        <CenteredLoading>
                            <CircularProgress />
                        </CenteredLoading>
                    ) : isMobile ? (
                        <>
                            {clients.length > 0 ? (
                                <MobileCardList>
                                    {clients.map((client) => (
                                        <ClientRowCard key={client._id} variant="outlined">
                                            <CardInfo>
                                                <ClientNameRow>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {client.name}
                                                    </Typography>
                                                    {!client.billingContact && (
                                                        <HoverTooltip title="Missing billing contact email — invoices cannot be emailed to this client">
                                                            <NoBillingEmailChip icon={<ErrorOutline />} label="No billing email" color="warning" size="small" variant="outlined" />
                                                        </HoverTooltip>
                                                    )}
                                                </ClientNameRow>
                                                <Typography variant="body2" color="text.secondary">
                                                    {client.billingContact || client.email || dash}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {client.phone || dash}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {[cityState(client) === dash ? null : cityState(client), client.address?.country].filter(Boolean).join(" • ") || dash}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Last activity: {formatActivityDate(client.lastActivityDate) || dash}
                                                </Typography>
                                            </CardInfo>
                                            {rowActions(client)}
                                        </ClientRowCard>
                                    ))}
                                </MobileCardList>
                            ) : (
                                <EmptyMobileState>
                                    <Typography variant="body2" color="text.secondary">
                                        {emptyCopy}
                                    </Typography>
                                </EmptyMobileState>
                            )}
                            {total > limit && (
                                <PaginationRow>
                                    <Pagination count={Math.max(1, Math.ceil(total / limit))} page={page} onChange={(e, v) => setPage(v)} color="primary" />
                                </PaginationRow>
                            )}
                        </>
                    ) : (
                        <>
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sortDirection={sortBy === "name" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("name")}>Client</TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={sortBy === "email" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("email")}>Billing email</TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={sortBy === "phone" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("phone")}>Phone</TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={sortBy === "city" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("city")}>City/State</TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={sortBy === "zip" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("zip")}>ZIP</TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={sortBy === "country" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("country")}>Country</TableSortLabel>
                                            </TableCell>
                                            <TableCell>Manager</TableCell>
                                            <TableCell sortDirection={sortBy === "billingType" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("billingType")}>Bill type</TableSortLabel>
                                            </TableCell>
                                            <TableCell sortDirection={sortBy === "lastActivity" ? sortDir : false}>
                                                <TableSortLabel {...sortLabelProps("lastActivity")}>Last activity</TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {clients.length > 0 ? (
                                            clients.map((client) => (
                                                <TableRow key={client._id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {client.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{billingEmailCell(client)}</TableCell>
                                                    <TableCell>{client.phone || dash}</TableCell>
                                                    <TableCell>{cityState(client)}</TableCell>
                                                    <TableCell>{client.address?.postalCode || dash}</TableCell>
                                                    <TableCell>{client.address?.country || dash}</TableCell>
                                                    <TableCell>{client.clientManager?.fullName || dash}</TableCell>
                                                    <TableCell>{client.billingTerms?.type || dash}</TableCell>
                                                    <TableCell>
                                                        {client.lastActivityDate ? <Typography variant="body2">{formatActivityDate(client.lastActivityDate)}</Typography> : dash}
                                                    </TableCell>
                                                    <TableCell align="right">{rowActions(client)}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center">
                                                    <EmptyMessage variant="body2" color="text.secondary">
                                                        {emptyCopy}
                                                    </EmptyMessage>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {total > limit && (
                                <PaginationRow>
                                    <Pagination count={Math.max(1, Math.ceil(total / limit))} page={page} onChange={(e, v) => setPage(v)} color="primary" />
                                </PaginationRow>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
            <EditBillingClientDialog open={Boolean(editClientId)} clientId={editClientId} onClose={() => setEditClientId(null)} onSaved={loadClients} />
            <ContactClientDialog open={Boolean(contactTarget)} client={contactTarget} onClose={() => setContactTarget(null)} />
            <BillingPreviewDialog open={Boolean(previewClient)} client={previewClient} onClose={() => setPreviewClient(null)} />
        </ClientsTabPanel>
    );
};

export default BillingClients;
